import React from 'react';

const TermsOfService = () => {
    return (
        <div className="container mx-auto p-6 max-w-4xl">
            <h1 className="text-4xl font-bold mb-6 text-gray-900">Terms of Service</h1>
            <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 mb-8">
                    Last Updated: January 2025
                </p>

                <div className="mb-8">
                    <p className="mb-4">
                        Welcome to CVSeeyou ("we," "our," or "us"). By accessing or using our platform, you agree to these Terms of Service ("Terms"). Please read these Terms carefully before using our services.
                    </p>
                </div>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Acceptance of Terms</h2>
                    <p className="mb-4">
                        By accessing or using CVSeeyou, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, and any additional guidelines or rules applicable to specific services. If you do not agree to these Terms, please do not use our platform.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">2. Service Description</h2>
                    <p className="mb-4">
                        CVSeeyou is a recruitment platform powered by Google's Gemini AI technology that facilitates connections between job seekers and recruiters. Our services include, but are not limited to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Resume and job description analysis using Gemini AI technology</li>
                        <li>Secure document storage in encrypted S3 buckets</li>
                        <li>AI-powered matching algorithms and recommendations</li>
                        <li>Recruitment process automation and optimization</li>
                    </ul>
                    <p className="mb-4">
                        Our platform utilizes Google's Gemini AI technology to process and analyze documents and generate matches. By using our service, you acknowledge that your data may be processed by Gemini AI in accordance with Google's AI principles and privacy standards.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">3. User Accounts and Security</h2>
                    <p className="mb-4">
                        3.1. You must be at least 18 years old to use our services.
                    </p>
                    <p className="mb-4">
                        3.2. You are responsible for:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Maintaining the confidentiality of your account credentials</li>
                        <li>Restricting access to your account</li>
                        <li>All activities that occur under your account</li>
                        <li>Notifying us immediately of any unauthorized access</li>
                    </ul>
                    <p className="mb-4">
                        3.3. We reserve the right to suspend or terminate accounts that violate these Terms or engage in suspicious activity.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Data Processing and AI Technology</h2>
                    <p className="mb-4">
                        4.1. Our platform uses Google's Gemini AI technology to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Analyze resumes and job descriptions</li>
                        <li>Generate matches between candidates and positions</li>
                        <li>Provide insights and recommendations</li>
                        <li>Optimize search results and rankings</li>
                    </ul>
                    <p className="mb-4">
                        4.2. By using our service, you acknowledge and agree that:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Your documents will be processed by Gemini AI technology</li>
                        <li>AI-generated results are recommendations and should not be the sole basis for hiring decisions</li>
                        <li>You will review all AI-generated matches and recommendations before making decisions</li>
                        <li>We do not guarantee the accuracy of AI-generated results</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">5. User Content and Conduct</h2>
                    <p className="mb-4">
                        5.1. You retain ownership of your content but grant us a worldwide, non-exclusive license to use, store, and process your content for providing our services.
                    </p>
                    <p className="mb-4">
                        5.2. Prohibited activities include:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Uploading false, misleading, or fraudulent information</li>
                        <li>Violating intellectual property rights</li>
                        <li>Attempting to bypass platform security measures</li>
                        <li>Engaging in discriminatory practices</li>
                        <li>Using automated scripts without our express permission</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Payments and Subscription</h2>
                    <p className="mb-4">
                        6.1. Payment terms, including subscription fees and billing cycles, will be clearly displayed before purchase.
                    </p>
                    <p className="mb-4">
                        6.2. Subscriptions automatically renew unless cancelled at least 24 hours before the renewal date.
                    </p>
                    <p className="mb-4">
                        6.3. Refunds are provided in accordance with applicable laws and our refund policy.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Limitation of Liability</h2>
                    <p className="mb-4">
                        7.1. Our services are provided "as is" without warranties of any kind, either express or implied.
                    </p>
                    <p className="mb-4">
                        7.2. We shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Changes to Terms</h2>
                    <p className="mb-4">
                        We may modify these Terms at any time. We will notify users of material changes via email or platform notification at least 30 days before implementation. Continued use of our services after changes constitute acceptance of the modified Terms.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Dispute Resolution</h2>
                    <p className="mb-4">
                        9.1. These Terms are governed by the laws of NSW, Australia.
                    </p>
                    <p className="mb-4">
                        9.2. Any disputes shall first be attempted to be resolved through mediation before pursuing legal action.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Contact Information</h2>
                    <p className="mb-4">
                        For questions about these Terms or our services, please contact us at:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p>Email: support@cvseeyou.com</p>
                        <p>Address: 11 York Street, Sydney, NSW 2000</p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default TermsOfService;